import { Injectable, StaticClassProvider } from '@angular/core';
import { Store } from '@ngrx/store';

import { defaultBcpLocale } from '../../definitions';
import { LocaleState } from '../../redux/state';
import { setLocaleAction, setLockedLocaleAction } from '../../redux/actions';
import { LocaleResolverService } from '../locale-resolver/locale-resolver.service';

@Injectable()
export class InitialLocaleLoaderService {
  constructor(private readonly store: Store<LocaleState>, private readonly resolver: LocaleResolverService) {}

  /**
   * Sets the app locale to an initial value, based on the URL and Session-Storage if applicable.
   * Note: To be called only from the Root Module's AppComponent.
   */
  init() {
    // first check for a locked locale. If none is present, proceed with normal ways to set the locale.
    const lockLocale = this.lookupLockLocaleFromUrl();
    if (lockLocale) {
      this.store.dispatch(setLockedLocaleAction({ bcpLocale: lockLocale }));
      return;
    }

    // highest priority left, if null is returned we try further to the right:
    const locale = this.lookupLocaleFromUrl() || this.lookupLocaleFromStorage() || defaultBcpLocale;
    this.store.dispatch(setLocaleAction({ bcpLocale: locale }));
  }

  /**
   * Determines the locale based on a previous resolution saved in the Session-Storage.
   */
  private lookupLocaleFromStorage(): string | null {
    return window.sessionStorage.getItem('preferredLocale');
  }

  /**
   * Determines the locked locale based on a query part of the current site URL
   *
   * End User note: Append lockLocale=<bcp-code> to your URL to trigger this, i.e.
   *  https://iev666.amadeus.com:4200/tasks -> https://iev666.amadeus.com:4200/tasks?lockLocale=DE-de
   * Please note: This is only intended for debugging, not for normal usage.
   */
  private lookupLockLocaleFromUrl(): string | null {
    return this.getUrlParam('lockLocale');
  }

  /**
   * Determines the locale based on a query part of the current site URL
   *
   * End User note: Append LANGUAGE=<si-code> to your URL to trigger this, i.e.
   *  https://iev666.amadeus.com:4200/tasks -> https://iev666.amadeus.com:4200/tasks?LANGUAGE=FR
   */
  private lookupLocaleFromUrl(): string | null {
    const siLocale = this.getUrlParam('LANGUAGE');
    if (!siLocale) {
      return null;
    }

    const bcpLocale = this.resolver.resolveFromSiToBcpOrNull(siLocale);
    return bcpLocale;
  }

  /**
   * Helper method to parse out a parameter from the current site's query string
   * @param key parameter name to look for
   */
  private getUrlParam(key: string): string | null {
    // having the hash first and then the query confuses URLSearchParams, so we have to "sanatize" the
    //  URL string first
    const urlQuery = window.location.hash.split('?').pop() || '';
    const urlParams = new URLSearchParams(urlQuery);

    return urlParams.get(key);
  }
}

export const InitialLocaleLoaderServiceProvider: StaticClassProvider = {
  provide: InitialLocaleLoaderService,
  useClass: InitialLocaleLoaderService,
  deps: [Store, LocaleResolverService]
};
